import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN, stickerButton } from "../../theme";
import { COUNTING_ENGINE_THEME, countingChoices, countingHiddenComposition } from "../../../../data/countingLadderCatalog";
import { COUNTING_PIECE } from "../../media/learnAssets";
import { ConfettiBurst } from "../../feedback/Celebration";
import { CARD_FACE, CountingPanel, CountingPieceGrid, CountingRoot, EDGE_IDLE, NumberCard, PIECE_NOUN, PanelChip, darkenHex, hexAlpha, useCompleteOnce } from "../CountingPiece";

/**
 * Keep a total in your head: count the pieces, drape the cloth over them,
 * then say how many are hiding. Activity 68 leaves a few showing and asks
 * for the missing part.
 */
const HiddenSetGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion = false }) => {
  const composition = countingHiddenComposition(activity);
  const answer = composition.covered;
  const piece = activity.number === 38 ? "rabbit" : "pebble";
  const noun = PIECE_NOUN[piece];
  const accent = COUNTING_ENGINE_THEME.hiddenSet.accent;
  const [covered, setCovered] = useState(false);
  const [picked, setPicked] = useState(null);
  const [burst, setBurst] = useState(0);
  const complete = useCompleteOnce(onComplete);
  const solved = picked?.ok === true;

  const cover = () => {
    sfx?.("whoosh", { volume: 0.5 });
    setCovered(true);
  };

  const choose = (value) => {
    if (solved) return;
    if (value === answer) {
      setPicked({ value, ok: true });
      setCovered(false);
      sfx?.("correct");
      setBurst((b) => b + 1);
      complete();
      return;
    }
    setPicked({ value, ok: false });
    onWrong?.();
    setTimeout(() => setPicked((p) => (p?.value === value && !p.ok ? null : p)), 600);
  };

  return (
    <CountingRoot>
      <CountingPanel
        grow
        tint={accent}
        chip={<PanelChip text={solved ? "Found them" : covered ? "How many hid?" : "Count, then cover"} accent={accent} />}
        style={{ flexDirection: "row", gap: "calc(var(--tile) * 0.16)", alignItems: "stretch" }}
      >
        <div
          style={{
            position: "relative", flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden",
            borderRadius: LEARN.radius.md, background: hexAlpha(accent, 0.08), boxShadow: `inset 0 0 0 2px ${hexAlpha(accent, 0.18)}`, padding: 10, boxSizing: "border-box",
          }}
        >
          <CountingPieceGrid
            count={composition.covered}
            asset={piece}
            countable={!covered && !solved}
            speak={speak}
            sfx={sfx}
            accent={accent}
            reduceMotion={reduceMotion}
          />
          <AnimatePresence>
            {covered && (
              <motion.div
                key="cloth"
                role="img"
                aria-label={`Cloth covering the hidden ${noun}s`}
                initial={reduceMotion ? false : { y: "-112%", rotate: -5, scaleY: 1.04 }}
                animate={{ y: 0, rotate: 0, scaleY: 1 }}
                exit={reduceMotion ? { opacity: 0 } : { y: "-112%", rotate: 4, opacity: 0.8 }}
                transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 200, damping: 22 }}
                style={{
                  position: "absolute", inset: 0, borderRadius: LEARN.radius.md, overflow: "hidden", transformOrigin: "50% 0%",
                  background: `linear-gradient(135deg, ${accent}, ${hexAlpha(accent, 0.85)} 55%, ${COUNTING_ENGINE_THEME.hiddenSet.secondary})`,
                  boxShadow: "0 14px 28px rgba(11,26,51,0.3), inset 0 0 0 3px rgba(255,255,255,0.7), inset 0 -18px 30px rgba(11,26,51,0.12)",
                }}
              >
                <img src={COUNTING_PIECE("cloth")} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.75, pointerEvents: "none" }} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {composition.showing > 0 && (
          <div
            aria-label={`${composition.showing} ${noun}s showing`}
            style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "calc(var(--tile) * 1.7)", flexShrink: 0, minHeight: 0, padding: 10, boxSizing: "border-box", borderRadius: LEARN.radius.md, background: CARD_FACE, border: `2px dashed ${hexAlpha(accent, 0.5)}`, boxShadow: `inset 0 2px 0 rgba(255,255,255,0.9), 0 4px 0 ${EDGE_IDLE}` }}
          >
            <CountingPieceGrid count={composition.showing} asset={piece} columns={1} gapRatio={0.2} countable={!covered && !solved} speak={speak} sfx={sfx} accent={accent} reduceMotion={reduceMotion} />
          </div>
        )}
        {burst > 0 && <ConfettiBurst key={`burst-${burst}`} count={28} originY={40} reduceMotion={reduceMotion} />}
      </CountingPanel>

      <div style={{ minHeight: "calc(var(--tile) * 1.4)", display: "flex", alignItems: "center", justifyContent: "center", gap: "calc(var(--tile) * 0.16)", flexWrap: "wrap" }}>
        {!covered && !solved ? (
          <button type="button" className="counting-btn" onClick={cover} style={{ ...stickerButton({ bg: accent, deep: darkenHex(accent), size: "lg" }) }}>
            Cover them
          </button>
        ) : (
          countingChoices(answer, 10).map((value) => (
            <NumberCard
              key={value}
              accent={accent}
              reduceMotion={reduceMotion}
              disabled={solved}
              state={picked?.value === value ? (picked.ok ? "correct" : "wrong") : "idle"}
              onClick={() => choose(value)}
            >
              {value}
            </NumberCard>
          ))
        )}
      </div>
    </CountingRoot>
  );
};

export default HiddenSetGame;
